import { useEffect, useState } from "react";
import { Plus, Flag, X, CheckCircle2, Circle, Trash2 } from "lucide-react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "@/components/ui/dialog";
import { toast } from "sonner";

export default function Goals() {
  const [goals, setGoals] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [title, setTitle] = useState("");
  const [steps, setSteps] = useState([""]);

  const load = async () => {
    try {
      const { data } = await api.get("/goals");
      setGoals(data);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, []);

  const resetForm = () => { setTitle(""); setSteps([""]); };

  const create = async () => {
    try {
      const cleaned = steps.map((s) => s.trim()).filter(Boolean);
      const { data } = await api.post("/goals", { title: title.trim(), steps: cleaned });
      setGoals((prev) => [...prev, data]);
      setOpen(false); resetForm();
      toast.success("New goal added.");
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    }
  };

  const toggleStep = async (goal, stepId) => {
    const updated = goal.steps.map((s) => s.id === stepId ? { ...s, done: !s.done } : s);
    try {
      const { data } = await api.patch(`/goals/${goal.id}`, { steps: updated });
      setGoals((prev) => prev.map((g) => (g.id === data.id ? data : g)));
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    }
  };

  const removeGoal = async (id) => {
    try {
      await api.delete(`/goals/${id}`);
      setGoals((prev) => prev.filter((g) => g.id !== id));
      toast.success("Goal removed.");
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    }
  };

  return (
    <div>
      <div className="flex items-start sm:items-center justify-between flex-col sm:flex-row gap-3 mb-6">
        <div>
          <h1 className="font-heading font-bold text-3xl sm:text-4xl text-slate-800">My goals</h1>
          <p className="text-slate-600 mt-1">Big things feel easier as small steps.</p>
        </div>
        <Button onClick={() => { resetForm(); setOpen(true); }} className="rounded-full bg-amber-500 hover:bg-amber-600 text-white" data-testid="add-goal-btn">
          <Plus className="w-4 h-4 mr-1" /> Add goal
        </Button>
      </div>

      {loading ? (
        <div className="text-slate-500">Loading your goals...</div>
      ) : goals.length === 0 ? (
        <div className="bg-white rounded-2xl border border-dashed border-slate-300 p-10 text-center" data-testid="goals-empty">
          <Flag className="w-8 h-8 text-slate-400 mx-auto mb-2" />
          <h3 className="font-heading font-bold text-slate-800">No goals yet</h3>
          <p className="text-sm text-slate-600 mt-1">Add one small goal to start. Even one step counts.</p>
        </div>
      ) : (
        <div className="grid md:grid-cols-2 gap-4" data-testid="goals-list">
          {goals.map((g) => {
            const done = g.steps.filter((s) => s.done).length;
            const total = g.steps.length;
            return (
              <div key={g.id} data-testid={`goal-${g.id}`} className="bg-white rounded-2xl border border-slate-200 p-5">
                <div className="flex items-start justify-between gap-2 mb-3">
                  <div className="flex items-center gap-2">
                    <div className="w-9 h-9 rounded-xl bg-amber-100 flex items-center justify-center">
                      <Flag className="w-5 h-5 text-amber-700" />
                    </div>
                    <div>
                      <h3 className="font-heading font-bold text-slate-800">{g.title}</h3>
                      <p className="text-xs text-slate-500">{done} of {total} steps done</p>
                    </div>
                  </div>
                  <button
                    onClick={() => removeGoal(g.id)}
                    data-testid={`delete-goal-${g.id}`}
                    className="text-slate-400 hover:text-red-600 p-1 rounded"
                    aria-label="Delete goal"
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
                <ul className="space-y-2">
                  {g.steps.map((s) => (
                    <li key={s.id}>
                      <button
                        onClick={() => toggleStep(g, s.id)}
                        data-testid={`step-${s.id}`}
                        className="w-full flex items-center gap-2 text-left p-2 rounded-lg hover:bg-slate-50"
                      >
                        {s.done
                          ? <CheckCircle2 className="w-5 h-5 text-emerald-600 shrink-0" />
                          : <Circle className="w-5 h-5 text-slate-400 shrink-0" />}
                        <span className={`text-sm ${s.done ? "line-through text-slate-400" : "text-slate-700"}`}>{s.text}</span>
                      </button>
                    </li>
                  ))}
                </ul>
              </div>
            );
          })}
        </div>
      )}

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="bg-white sm:max-w-lg rounded-2xl" data-testid="goal-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading">Add a new goal</DialogTitle>
            <DialogDescription>Give it a name. Break it into small steps.</DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label htmlFor="goal-title">Goal name</Label>
              <Input
                id="goal-title" data-testid="goal-title-input"
                value={title} onChange={(e) => setTitle(e.target.value)}
                placeholder="Learn to cook pasta" className="mt-1 rounded-xl h-11"
              />
            </div>
            <div>
              <Label>Small steps</Label>
              <div className="space-y-2 mt-1">
                {steps.map((s, i) => (
                  <div key={i} className="flex gap-2">
                    <Input
                      value={s}
                      data-testid={`goal-step-input-${i}`}
                      onChange={(e) => setSteps((prev) => prev.map((x, j) => (i === j ? e.target.value : x)))}
                      placeholder={`Step ${i + 1}`} className="rounded-xl h-11"
                    />
                    {steps.length > 1 && (
                      <button
                        onClick={() => setSteps((prev) => prev.filter((_, j) => j !== i))}
                        data-testid={`remove-step-${i}`}
                        className="text-slate-400 hover:text-red-600 p-2"
                        aria-label="Remove step"
                      >
                        <X className="w-4 h-4" />
                      </button>
                    )}
                  </div>
                ))}
                <Button variant="outline" className="rounded-full" onClick={() => setSteps((p) => [...p, ""])} data-testid="add-step-btn">
                  <Plus className="w-4 h-4 mr-1" /> Add another step
                </Button>
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setOpen(false)} className="rounded-full" data-testid="goal-cancel-btn">Cancel</Button>
            <Button
              onClick={create}
              disabled={!title.trim()}
              className="rounded-full bg-amber-500 hover:bg-amber-600 text-white"
              data-testid="goal-save-btn"
            >
              Save goal
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
